import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Calendar, MessageCircle, ClipboardList, Compass } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";

const STEPS = [
  { icon: ClipboardList, k: "01", t: "Complete a quick intake",
    d: "Tell me about your service, your current rating, and what you're trying to accomplish. This helps me arrive at our call already understanding your file." },
  { icon: Calendar, k: "02", t: "Book a free 20-minute consult",
    d: "Video or phone. No fee, no pitch. We talk through your goals and I tell you honestly whether the VDE approach is right for you." },
  { icon: MessageCircle, k: "03", t: "We build a plan together",
    d: "If we're a good fit, I'll walk you through the coaching options that make sense for your specific claim and your specific timeline." },
  { icon: Compass, k: "04", t: "You execute, with guidance",
    d: "Lay statements, nexus letters, buddy statements, records mining. Real strategy, not guesswork, every step of the way." },
];

export default function WorkWithMe() {
  useSeo({
    title: "Work With Me | Veterans Disability Education",
    description: "A free 20-minute consult, an honest read on your file, and a claim strategy built before you file. No percentage fees. No pressure.",
  });
  return (
    <div>
      <section className="bg-primary text-primary-foreground">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-20">
          <div className="label-caps text-accent mb-5">Work With Me</div>
          <h1 className="font-display text-4xl lg:text-6xl leading-[1.05] max-w-4xl">
            Every claim is different. <span className="text-accent">Let's talk first.</span>
          </h1>
          <p className="mt-7 text-lg text-primary-foreground/90 max-w-3xl">
            I don't quote or sell anything to a veteran I haven't spoken with. Every service member's file, service history, and goals are different, and pricing without understanding your situation is exactly the kind of thing predatory claims consultants do. That's not me.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row gap-4">
            <Button asChild size="lg" data-testid="wwm-cta-intake">
              <Link to="/intake">Start Your Free Consult <ArrowRight className="w-4 h-4" /></Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-primary">
              <Link to="/calculator">Estimate your compensation</Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-20">
          <div className="max-w-3xl mb-12">
            <div className="label-caps mb-4">How it works</div>
            <h2 className="font-display text-3xl lg:text-5xl">Four simple steps.</h2>
            <p className="mt-5 text-lg text-primary/80">
              You do steps one and two before we talk about anything else. If I can't help you, I'll tell you and point you to someone who can.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {STEPS.map((s) => (
              <div key={s.k} className="bg-secondary p-8 border-l-4 border-accent" data-testid={`wwm-step-${s.k}`}>
                <div className="flex items-baseline justify-between mb-6">
                  <span className="font-mono text-xs text-primary/60">STEP {s.k}</span>
                  <s.icon className="w-6 h-6 text-primary" strokeWidth={1.6} />
                </div>
                <h3 className="font-display text-2xl mb-3 leading-snug">{s.t}</h3>
                <p className="text-[16px] text-primary/85 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-secondary">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-20">
          <div className="max-w-3xl">
            <div className="label-caps mb-4">Straight talk</div>
            <h2 className="font-display text-3xl lg:text-4xl">What VDE is, and what VDE is not.</h2>
          </div>
          <div className="mt-10 grid md:grid-cols-2 gap-6">
            <div className="bg-white p-8 border-t-4 border-primary">
              <h3 className="font-display text-xl mb-4">What VDE does</h3>
              <ul className="space-y-3 text-[16px] text-primary/85">
                <li className="flex gap-3"><span className="text-accent mt-1">▸</span><span>Educates you on how VA disability claims actually get won.</span></li>
                <li className="flex gap-3"><span className="text-accent mt-1">▸</span><span>Coaches you through writing strong lay statements.</span></li>
                <li className="flex gap-3"><span className="text-accent mt-1">▸</span><span>Guides you on securing nexus letters that meet VA standards.</span></li>
                <li className="flex gap-3"><span className="text-accent mt-1">▸</span><span>Helps you mine your medical records for evidence you didn't know was there.</span></li>
                <li className="flex gap-3"><span className="text-accent mt-1">▸</span><span>Structures buddy and witness statements so they actually count.</span></li>
              </ul>
            </div>
            <div className="bg-white p-8 border-t-4 border-primary/40">
              <h3 className="font-display text-xl mb-4 text-primary/80">What VDE does not do</h3>
              <ul className="space-y-3 text-[16px] text-primary/85">
                <li className="flex gap-3"><span className="text-primary/40 mt-1">▸</span><span>File claims on your behalf (that's a job for a VA-accredited VSO, free at va.gov).</span></li>
                <li className="flex gap-3"><span className="text-primary/40 mt-1">▸</span><span>Charge a percentage of your backpay or any outcome-based fee.</span></li>
                <li className="flex gap-3"><span className="text-primary/40 mt-1">▸</span><span>Guarantee any specific rating or benefit amount, no one honest can.</span></li>
                <li className="flex gap-3"><span className="text-primary/40 mt-1">▸</span><span>Provide legal or medical advice.</span></li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-20 text-center">
          <div className="gold-rule mx-auto mb-6"></div>
          <h2 className="font-display text-3xl lg:text-5xl max-w-3xl mx-auto">The best next step is a conversation.</h2>
          <p className="mt-5 text-lg text-primary/80 max-w-2xl mx-auto">
            No pressure, no fee, just an honest read on whether we're a good fit.
          </p>
          <Button asChild size="lg" className="mt-9" data-testid="wwm-footer-cta">
            <Link to="/intake">Start Your Free Consult <ArrowRight className="w-4 h-4" /></Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
